import { Flatfile } from "@flatfile/api";
import { WSQueryConfig } from "@ws-react-query";
import { IBulkBatch } from "@wingspanhq/payments/dist/interfaces";
import { BATCH_LABELS_WS_KEYS } from "@wingspanhq/payments/dist/lib/constants";
import { useWSQuery } from "../../../../query/helpers";
import { FLATFILE_UPLOAD_JOB_REFETCH_INTERVAL } from "../../constants";
import { useFlatfileApiClient } from "../../hooks/useFlatfileApiClient";
import { isUploadInProgress } from "../../utils/bulkBatchUtils";
import { QUERY_FLATFILE_UPLOAD_JOB } from "./keys";

const FINISHED_UPLOAD_JOB_STATUSES: Flatfile.JobStatus[] = [
  Flatfile.JobStatus.Complete,
  Flatfile.JobStatus.Failed,
  Flatfile.JobStatus.Canceled
];

const isUploadJobFinished = (uploadJob?: Flatfile.Job): boolean =>
  !!uploadJob?.status &&
  FINISHED_UPLOAD_JOB_STATUSES.includes(uploadJob.status);

/**
 * Flatfile job that uploads the spreadsheet rows into the bulk batch (the parent "main action"
 * job). It is the only source of upload progress and of upload failures: when the job dies, the
 * batch keeps its `flatfileJobId` label and never receives `uploadFinishedAt`.
 */
export const useQueryFlatfileUploadJob = (
  bulkBatch?: IBulkBatch,
  config?: WSQueryConfig<Flatfile.Job, Error>
) => {
  const flatfileApiClient = useFlatfileApiClient(bulkBatch);
  const jobId = bulkBatch?.labels?.[BATCH_LABELS_WS_KEYS.flatfileJobId] as
    | string
    | undefined;

  return useWSQuery<Flatfile.Job, Error>(
    [QUERY_FLATFILE_UPLOAD_JOB, jobId],
    async () => {
      const { data: job } = await flatfileApiClient!.jobs.get(jobId!);
      return job;
    },
    {
      // the job is only interesting while the upload runs; afterwards the batch labels tell the
      // whole story, so finished batches must not hit Flatfile on every detail page view
      enabled: !!flatfileApiClient && !!jobId && isUploadInProgress(bulkBatch),
      // keep polling while the upload is running, the job is the only thing that moves
      refetchInterval: uploadJob =>
        isUploadInProgress(bulkBatch) && !isUploadJobFinished(uploadJob)
          ? FLATFILE_UPLOAD_JOB_REFETCH_INTERVAL
          : false,
      retry: false,
      ...config
    }
  );
};
